// src/services/ecoReportCacheService.js

/**
 * Service to handle caching of eco report data
 * Saves and retrieves data from localStorage to reduce API calls
 */
const WILDLIFE_CACHE_KEY = 'garden-eco-wildlife-cache';
const ENV_TIPS_CACHE_KEY = 'garden-eco-tips-cache';
const CACHE_META_KEY = 'garden-eco-cache-meta';

// 24 hours in milliseconds
const CACHE_MAX_AGE = 24 * 60 * 60 * 1000;

/**
 * Calculate a hash for the plant collection to detect changes
 * @param {Array} plants - Array of plants
 * @returns {string} - A hash string representing the plants collection
 */
const calculatePlantsHash = (plants) => {
  if (!plants || !Array.isArray(plants) || plants.length === 0) {
    return '0'; // Return default hash for empty plants
  }
  
  try {
    // Create a safer sorting function that handles different ID types
    const sortedPlants = [...plants].sort((a, b) => {
      // Handle when either a or b has no id
      if (!a.id && !b.id) return 0;
      if (!a.id) return -1;
      if (!b.id) return 1;
      
      // Convert IDs to strings to ensure compatibility
      const idA = String(a.id);
      const idB = String(b.id);
      
      // Now compare the string IDs
      return idA.localeCompare(idB);
    });
    
    // Create a string with plant ids and their last update timestamps
    // Also add fallback handling for missing properties
    const plantsString = sortedPlants.map(p => {
      const id = p.id ? String(p.id) : 'unknown';
      const update = p.lastUpdated || '';
      const type = p.type || '';
      return `${id}-${type}-${update}`;
    }).join('|');
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < plantsString.length; i++) {
      const char = plantsString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString();
  } catch (error) {
    console.error('Error calculating plants hash:', error);
    // Return a fallback hash that's unique for this set of plants
    return Date.now().toString();
  }
};

/**
 * Save metadata about the cache
 * @param {string} plantsHash - Hash representing the current plants collection
 */
const saveCacheMetadata = (plantsHash) => {
  try {
    const metadata = {
      plantsHash: plantsHash || Date.now().toString(), // Fallback if hash is missing
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_META_KEY, JSON.stringify(metadata));
  } catch (error) {
    console.error('Error saving cache metadata:', error);
  }
};

/**
 * Get cache metadata
 * @returns {Object|null} - Cache metadata or null if not found
 */
const getCacheMetadata = () => {
  const metaString = localStorage.getItem(CACHE_META_KEY);
  if (!metaString) return null;
  
  try {
    return JSON.parse(metaString);
  } catch (e) {
    console.error('Failed to parse cache metadata:', e);
    return null;
  }
};

/**
 * Check if cache is valid based on plants hash and timestamp
 * @param {Array} plants - Current plants array
 * @returns {boolean} - Whether the cache is valid
 */
const isCacheValid = (plants) => {
  try {
    const metadata = getCacheMetadata();
    if (!metadata) return false;
    
    // Check timestamp first (faster check)
    const isFresh = (Date.now() - metadata.timestamp) < CACHE_MAX_AGE;
    if (!isFresh) return false;
    
    // Only calculate hash if timestamp is fresh (more expensive operation)
    const currentPlantsHash = calculatePlantsHash(plants);
    const isSamePlants = metadata.plantsHash === currentPlantsHash;
    
    return isSamePlants && isFresh;
  } catch (error) {
    console.error('Error checking cache validity:', error);
    return false; // On any error, invalidate cache to be safe
  }
};

const ecoReportCacheService = {
  /**
   * Saves wildlife spotlight data to cache
   * @param {Array} plants - Current plants array for hash calculation
   * @param {Array} wildlifeData - Wildlife spotlight data to cache
   */
  saveWildlifeSpotlight: (plants, wildlifeData) => {
    try {
      if (!plants || !Array.isArray(plants)) {
        console.warn('Invalid plants data provided to saveWildlifeSpotlight');
        return;
      }
      
      if (!wildlifeData || !Array.isArray(wildlifeData)) {
        console.warn('Invalid wildlife data provided to saveWildlifeSpotlight');
        return;
      }
      
      // Store the data
      localStorage.setItem(WILDLIFE_CACHE_KEY, JSON.stringify(wildlifeData));
      
      // Calculate hash and save metadata
      try {
        const plantsHash = calculatePlantsHash(plants);
        saveCacheMetadata(plantsHash);
      } catch (hashError) {
        console.error('Error calculating plants hash:', hashError);
        // Save metadata with timestamp but use timestamp as hash in case of error
        saveCacheMetadata(Date.now().toString());
      }
    } catch (e) {
      console.error('Failed to cache wildlife spotlight data:', e);
    }
  },
  
  /**
   * Retrieves cached wildlife spotlight data if valid
   * @param {Array} plants - Current plants array to validate cache
   * @returns {Array|null} - Cached wildlife data or null if invalid/not found
   */
  getWildlifeSpotlight: (plants) => {
    if (!plants || !Array.isArray(plants)) {
      return null;
    }
    
    try {
      if (!isCacheValid(plants)) return null;
      
      const cached = localStorage.getItem(WILDLIFE_CACHE_KEY);
      if (!cached) return null;
      
      try {
        const parsedData = JSON.parse(cached);
        // Ensure we got an array
        if (Array.isArray(parsedData)) {
          return parsedData;
        }
        return null;
      } catch (parseError) {
        console.error('Failed to parse cached wildlife data:', parseError);
        return null;
      }
    } catch (e) {
      console.error('Failed to retrieve cached wildlife spotlight:', e);
      return null;
    }
  },
  
  /**
   * Saves environmental tips data to cache
   * @param {Array} plants - Current plants array for hash calculation
   * @param {Array} tipsData - Environmental tips data to cache
   */
  saveEnvironmentalTips: (plants, tipsData) => {
    try {
      if (!plants || !Array.isArray(plants)) {
        console.warn('Invalid plants data provided to saveEnvironmentalTips');
        return;
      }
      
      if (!tipsData || !Array.isArray(tipsData)) {
        console.warn('Invalid tips data provided to saveEnvironmentalTips');
        return;
      }
      
      // Store the data
      localStorage.setItem(ENV_TIPS_CACHE_KEY, JSON.stringify(tipsData));
      
      // Calculate hash and save metadata
      try {
        const plantsHash = calculatePlantsHash(plants);
        saveCacheMetadata(plantsHash);
      } catch (hashError) {
        console.error('Error calculating plants hash:', hashError);
        // Save metadata with timestamp but use timestamp as hash in case of error
        saveCacheMetadata(Date.now().toString());
      }
    } catch (e) {
      console.error('Failed to cache environmental tips data:', e);
    }
  },
  
  /**
   * Retrieves cached environmental tips if valid
   * @param {Array} plants - Current plants array to validate cache
   * @returns {Array|null} - Cached tips data or null if invalid/not found
   */
  getEnvironmentalTips: (plants) => {
    if (!plants || !Array.isArray(plants)) {
      return null;
    }
    
    try {
      if (!isCacheValid(plants)) return null;
      
      const cached = localStorage.getItem(ENV_TIPS_CACHE_KEY);
      if (!cached) return null;
      
      try {
        const parsedData = JSON.parse(cached);
        // Ensure we got an array
        if (Array.isArray(parsedData)) {
          return parsedData;
        }
        return null;
      } catch (parseError) {
        console.error('Failed to parse cached environmental tips:', parseError);
        return null;
      }
    } catch (e) {
      console.error('Failed to retrieve cached environmental tips:', e);
      return null;
    }
  },
  
  /**
   * Invalidates all eco report caches
   */
  invalidateCache: () => {
    localStorage.removeItem(WILDLIFE_CACHE_KEY);
    localStorage.removeItem(ENV_TIPS_CACHE_KEY);
    localStorage.removeItem(CACHE_META_KEY);
    localStorage.removeItem('garden-eco-report-cache');
    localStorage.removeItem('garden-eco-summary-cache');
  },

  /**
   * Expose the plants hash calculation function
   * @param {Array} plants - Plants array to calculate hash for
   * @returns {string} - A hash string representing the plants collection
   */
  calculatePlantsHash: calculatePlantsHash,

  /**
   * Expose the cache validation function
   * @param {Array} plants - Plants array to validate cache against
   * @returns {boolean} - Whether the cache is valid
   */
  isCacheValid: isCacheValid
};

export default ecoReportCacheService;